import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ShareIcon,
  MapPinIcon,
  CalendarIcon,
  HomeIcon,
  HeartIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const PublicObituaryDetail = ({ slug, config, onClose, onViewPlot }) => {
  const [obituary, setObituary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showShareToast, setShowShareToast] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchObituary();
    }
  }, [slug]);

  const fetchObituary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/public/obituaries/${slug}`);

      if (response.ok) {
        const data = await response.json();
        setObituary(data);
      } else if (response.status === 404) {
        setError('Obituary not found');
      } else {
        setError('Failed to load obituary');
      }
    } catch (err) {
      console.error('Error fetching obituary:', err);
      setError('Failed to load obituary');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return '';
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    if (includeTime) {
      options.hour = 'numeric';
      options.minute = '2-digit';
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getFullName = () => {
    if (!obituary) return '';
    const parts = [];
    if (obituary.firstName) parts.push(obituary.firstName);
    if (obituary.middleName) parts.push(obituary.middleName);
    if (obituary.lastName) parts.push(obituary.lastName);
    if (obituary.suffix) parts.push(obituary.suffix);
    return parts.join(' ');
  };

  const getLifespan = () => {
    if (!obituary) return '';
    const birth = obituary.dateOfBirth ? formatDate(obituary.dateOfBirth) : 'Unknown';
    const death = obituary.dateOfDeath ? formatDate(obituary.dateOfDeath) : 'Unknown';
    return `${birth} — ${death}`;
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/public?obituary=${slug}`;

    try {
      await navigator.clipboard.writeText(url);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const images = obituary?.attachments?.filter(a => a.attachmentType === 'image') || [];
  const documents = obituary?.attachments?.filter(a => a.attachmentType === 'document') || [];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !obituary) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
          <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || 'Obituary not found'}
          </h3>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{getFullName()}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Share"
            >
              <ShareIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Photo & Quick Info */}
            <div className="space-y-4">
              {/* Primary Photo / Gallery */}
              {images.length > 0 ? (
                <div className="space-y-2">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden relative">
                    <img
                      src={`/api/public/obituaries/${slug}/attachments/${images[selectedImageIndex].id}`}
                      alt={getFullName()}
                      className="w-full h-full object-cover"
                    />

                    {/* Gallery Navigation */}
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImageIndex(i => (i > 0 ? i - 1 : images.length - 1))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setSelectedImageIndex(i => (i < images.length - 1 ? i + 1 : 0))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                          {images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setSelectedImageIndex(idx)}
                              className={`w-2 h-2 rounded-full ${
                                idx === selectedImageIndex
                                  ? 'bg-white'
                                  : 'bg-white bg-opacity-50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="aspect-[3/4] bg-gray-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="h-20 w-20 text-gray-300" />
                </div>
              )}

              {/* Quick Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="text-center">
                  <p className="text-lg font-medium text-gray-900">{getFullName()}</p>
                  {obituary.maidenName && (
                    <p className="text-sm text-gray-500">née {obituary.maidenName}</p>
                  )}
                </div>

                <div className="text-center text-sm text-gray-600">
                  {getLifespan()}
                </div>

                {obituary.birthplace && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                    <span>Born in {obituary.birthplace}</span>
                  </div>
                )}

                {obituary.residence && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <HomeIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{obituary.residence}</span>
                  </div>
                )}

                {obituary.plotId && onViewPlot && (
                  <button
                    onClick={() => onViewPlot(obituary.plotId)}
                    className="w-full mt-2 px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 text-sm"
                  >
                    View Resting Place
                  </button>
                )}
              </div>
            </div>

            {/* Right Column - Biography & Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Biography */}
              {obituary.biography && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Obituary</h3>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                    {obituary.biography}
                  </div>
                </div>
              )}

              {/* Service Information */}
              {(obituary.serviceDate || obituary.serviceLocation || obituary.serviceNotes) && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-blue-600" />
                    Service Information
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    {obituary.serviceDate && (
                      <p>
                        <strong>Date:</strong> {formatDate(obituary.serviceDate, true)}
                      </p>
                    )}
                    {obituary.serviceLocation && (
                      <p>
                        <strong>Location:</strong> {obituary.serviceLocation}
                      </p>
                    )}
                    {obituary.serviceNotes && (
                      <div className="mt-2 whitespace-pre-wrap">{obituary.serviceNotes}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Memorial Information */}
              {(obituary.condolencesAddress || obituary.memorialDonations) && (
                <div className="bg-pink-50 border border-pink-100 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <HeartIcon className="h-5 w-5 text-pink-600" />
                    Memorial
                  </h3>
                  <div className="space-y-4 text-sm text-gray-700">
                    {obituary.condolencesAddress && (
                      <div>
                        <p className="font-medium mb-1">Send Condolences:</p>
                        <p className="whitespace-pre-wrap">{obituary.condolencesAddress}</p>
                      </div>
                    )}
                    {obituary.memorialDonations && (
                      <div>
                        <p className="font-medium mb-1">Memorial Donations:</p>
                        <p className="whitespace-pre-wrap">{obituary.memorialDonations}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents */}
              {documents.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Documents</h3>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={`/api/public/obituaries/${slug}/attachments/${doc.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                        <span className="text-sm text-gray-700">{doc.title || doc.fileName}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Toast */}
      {showShareToast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg">
          Link copied to clipboard
        </div>
      )}
    </div>
  );
};

export default PublicObituaryDetail;
